import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import EntityType from '@/shared/enums/EntityType';
import ScopeType from '@/shared/enums/ScopeType';
import useAccess from '@pkg/access/organisations/useAccess';
import useBudgetVisibility from './hooks/useBudgetVisibility';
import defaultScope from './utils/defaultScope';
import getSearchParams from './utils/getSearchParams';

const DEFAULT_SCOPE_ENTITY = EntityType.GROUP;

export const ViewModeContext = createContext();
export const useViewModeContext = () => useContext(ViewModeContext);

const ViewModeProvider = ({ children }) => {
  const router = useRouter();
  const access = useAccess();
  const hasAdminAccess = access.model.manage;
  const searchParams = useSearchParams();
  const params = getSearchParams(searchParams);
  const [view, setView] = useState(params.view);
  const [scope, setScope] = useState(params.scope);
  const [scopeEntity, setScopeEntity] = useState(params.scopeEntity);
  const [id, setId] = useState(params.id);
  const [settings, setSettings] = useState(params.settings);

  const { canAccessBudget, showBudget, updateBudgetVisibility } =
    useBudgetVisibility();

  // Resets the scope based on a newly selected view mode.
  const selectViewMode = useCallback((value) => {
    setView(value);
    const scope = defaultScope(value);
    setScope(scope);
    setScopeEntity(DEFAULT_SCOPE_ENTITY);
    setId(null);
  }, []);

  // Resets the scope entity based on the selected scope.
  const selectScope = (value) => {
    setScope(value);
    const redirectActivityScope =
      value === ScopeType.ROLES || value === ScopeType.SPANS_LAYERS;
    if (redirectActivityScope && scopeEntity === EntityType.ACTIVITY) {
      setScopeEntity(EntityType.GROUP);
      setId(null);
    }
  };

  // Updates the scope entity and resets the id.
  const selectScopeEntity = (value) => {
    setScopeEntity(value);
    setId(null);
  };

  const selectId = (scopeEntity, value) => {
    setId(value);
    setScopeEntity(scopeEntity ?? DEFAULT_SCOPE_ENTITY);
  };

  const selectSettings = (settings) => {
    setSettings(settings);
  };

  // This effect handles forward direction updates to the URL without causing
  // refetching of snapshot data.
  useEffect(() => {
    const originalParams = searchParams;
    const params = getSearchParams(searchParams);
    const viewChanged = originalParams.get('view') !== view;
    const scopeChanged = originalParams.get('scope') !== scope;
    const idChanged = originalParams.get(params.scopeEntity) !== id;
    const settingsChanged = originalParams.get('settings') !== settings;

    if (!viewChanged && !scopeChanged && !idChanged && !settingsChanged) {
      return;
    }

    // Reset the scope entity and id if the view or scope has changed.
    if (viewChanged) {
      params.scopeEntity = DEFAULT_SCOPE_ENTITY;
      params.id = null;
      setScopeEntity(params.scopeEntity);
      setId(params.id);
    }

    const updatedParams = new URLSearchParams(searchParams);

    updatedParams.set('view', view);
    updatedParams.set('scope', scope);

    if (scopeEntity && id) {
      updatedParams.set(scopeEntity, id);
    } else {
      if (params.scopeEntity) {
        updatedParams.delete(params.scopeEntity);
      }
    }

    if (settings) {
      updatedParams.set('settings', settings);
    } else {
      updatedParams.delete('settings');
    }

    const query = updatedParams.toString();

    router.push(`${window.location.pathname}?${query}`, { scroll: false });
  }, [id, settings, scope, scopeEntity, view]);

  // This effect handles historical navigation without causing refetching of
  // snapshot data.
  useEffect(() => {
    const syncFromHistory = () => {
      const historicalParams = new URLSearchParams(window.location.search);
      const params = getSearchParams(historicalParams);

      setView(params.view);
      setScope(params.scope);
      setScopeEntity(params.scopeEntity);

      if (params.id) {
        setId(params.id);
      } else {
        setId(null);
      }

      if (params.settings) {
        setSettings(params.settings);
      } else {
        setSettings(null);
      }
    };

    window.addEventListener('popstate', syncFromHistory);

    syncFromHistory();

    return () => window.removeEventListener('popstate', syncFromHistory);
  }, []);

  // Handle unauthorized access to spans scope.
  useEffect(() => {
    if (scope === ScopeType.SPANS_LAYERS && !hasAdminAccess) {
      selectScope(ScopeType.SUMMARY);
    }
  }, [scope, hasAdminAccess]);

  const context = useMemo(() => {
    return {
      canAccessBudget,
      id,
      scope,
      scopeEntity,
      selectViewMode,
      selectScope,
      selectScopeEntity,
      selectId,
      selectSettings,
      showBudget,
      updateBudgetVisibility,
      view,
    };
  }, [canAccessBudget, id, scope, scopeEntity, showBudget, view]);

  return (
    <ViewModeContext.Provider value={context}>
      {children}
    </ViewModeContext.Provider>
  );
};

export default ViewModeProvider;
